import * as React from 'react';
import { Title } from '../../utils/title';
import dayjs from 'dayjs';
import TimeAgo from 'react-timeago';
import { Link } from 'react-router-dom';
import { fetchApi } from '../../services/api';
import { openWindow } from '../../services/openWindow';
import { ILink } from '../../types/link';
import { Loader } from '@progress/kendo-react-indicators';

type NotificationSourceFilter = {
  value: number;
  label: string;
};

type AccountNotification = {
  Text: string;
  Body: string | null;
  IsModal: boolean;
  Link: string;
  PopupLink: string;
  CreatedDateTime: string | Date;
  Source: number;
  Links: ILink[];
};

type NotificationsResponse = {
  Notifications: AccountNotification[];
};

const sourceFilters: NotificationSourceFilter[] = [
  { value: 2, label: 'Quote Offers' },
  { value: 1, label: 'Order Events' },
  { value: 11, label: 'EDI' },
  { value: 5, label: 'Driver Chat' },
  { value: 10, label: 'Announcements' },
];

const AccountNotifications: React.FC = () => {
  const [selectedSource, setSelectedSource] = React.useState<number | null>(null);
  const [notifications, setNotifications] = React.useState<AccountNotification[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const loadNotifications = React.useCallback(async (source: number | null) => {
    setIsLoading(true);
    try {
      const endpoint = source === null
        ? '/api/Account/Notifications'
        : `/api/Account/Notifications?source=${source}`;

      const data = await fetchApi(endpoint) as NotificationsResponse;
      setNotifications(data.Notifications ?? []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadNotifications(selectedSource);
  }, [loadNotifications, selectedSource]);

  const activeNotifications = React.useMemo(
    () => notifications.filter((notification) => notification.Links.some((x) => x.Name === 'Dismiss')),
    [notifications]
  );

  const dismissNotification = React.useCallback((link: ILink | undefined) => {
    if (!link) return;

    fetchApi(link.Link, {}, link.Method).then(() => {
      loadNotifications(selectedSource);
    });
  }, [loadNotifications, selectedSource]);

  const dismissAllNotifications = React.useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    Promise.all(activeNotifications.map((notification) => {
      const link = notification.Links.find((x) => x.Name === 'Dismiss');
      return link ? fetchApi(link.Link, {}, link.Method) : Promise.resolve();
    })).then(() => {
      loadNotifications(selectedSource);
    });
  }, [activeNotifications, loadNotifications, selectedSource]);

  return <div className="container mt-3">
    <Title string="Notifications" />
    <div className="mb-3">
      <div className="btn-group flex-wrap" role="group" aria-label="Notification source filters">
        <button
          type="button"
          className={`btn ${selectedSource === null ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setSelectedSource(null)}
        >
          All
        </button>
        {sourceFilters.map((filter) => (
          <button
            key={filter.value}
            type="button"
            className={`btn ${selectedSource === filter.value ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setSelectedSource(filter.value)}
          >
            {filter.label}
          </button>
        ))}
      </div>
    </div>

    {isLoading && <div className="text-center mb-3">
      <Loader type="converging-spinner" />
    </div>}

    {!isLoading && notifications.length === 0 && (
      <div className="alert alert-secondary" role="alert">
        No notifications.
      </div>
    )}

    {notifications.map((notification, index) => (
      <div key={index.toString()} className="alert alert-info alert-dismissible fade show" role="alert">
        <h4 className="alert-heading">
          {notification.PopupLink ?
            <a href={notification.PopupLink} onClick={() => openWindow(notification.PopupLink)} className="alert-link">{notification.Text}</a>
            : notification.Link ? <Link to={notification.Link} className="alert-link">{notification.Text}</Link> : notification.Text}
        </h4>
        {notification.Body && <p>{notification.Body}</p>}
        <p className="mb-0"><TimeAgo date={dayjs.utc(notification.CreatedDateTime).toDate()} /></p>
        {notification.Links.find((x) => x.Name === 'Dismiss') && (
          <button
            onClick={() => dismissNotification(notification.Links.find((x) => x.Name === 'Dismiss'))}
            type="button"
            className="close"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        )}
      </div>
    ))}

    {activeNotifications.length > 0 && <div className="d-flex justify-content-center">
      <button className="btn btn-danger" onClick={dismissAllNotifications}>Clear All Notifications</button>
    </div>}
  </div>;
};

export default AccountNotifications;
